import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser, faEnvelope, faLock, faEye, faEyeSlash, faPhone, faGift } from '@fortawesome/free-solid-svg-icons';
import Button from '../../components/Button';
import useAuth from '../../hooks/useAuth';
import { apiRequest } from '../../config/api';
import { BR, US } from 'country-flag-icons/react/3x2';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';
import './signup.css';

const languageOptions = {
  pt: { label: 'Português', flag: BR, country: 'Brasil' },
  en: { label: 'English', flag: US, country: 'United States' },
};

const Signup = () => {
  const { signup } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [coupon, setCoupon] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [language, setLanguage] = useState(() => localStorage.getItem('language') || 'pt');
  const [showDropdown, setShowDropdown] = useState(false);
  const [userId, setUserId] = useState(null);
  const [idStripe, setIdStripe] = useState(null);
  const dropdownRef = useRef();
  const { t } = useTranslation();

  // Fecha dropdown ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Troca idioma globalmente
  const handleLanguageChange = (lang) => {
    setLanguage(lang);
    localStorage.setItem('language', lang);
    if (i18n.changeLanguage) {
      i18n.changeLanguage(lang);
    }
    setShowDropdown(false);
  };

  // Atualiza idioma ao montar componente
  useEffect(() => {
    if (i18n.changeLanguage) {
      i18n.changeLanguage(language);
    }
  }, [language]);

  // Busca o stripe_id do usuário após cadastro ou quando userId mudar
  useEffect(() => {
    const fetchIdStripe = async () => {
      if (userId) {
        try {
          const data = await apiRequest(`/users/${userId}/subscription-status`);
          if (data.success && data.data) {
            setIdStripe(data.data.stripe_id);
          }
        } catch (error) {
          console.error('Erro ao buscar stripe_id:', error);
        }
      }
    };
    fetchIdStripe();
  }, [userId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    let referalIdToUse = null;

    if (!name.trim() || !email.trim() || !phone.trim() || !password.trim()) {
      setError(t('signup.fillAll'));
      return;
    }

    if (password.length < 6) {
      setError(language === 'pt' ? 'A senha deve ter pelo menos 6 caracteres.' : 'Password must be at least 6 characters.');
      return;
    }

    // Validação básica de e-mail
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError(t('signup.validEmail'));
      return;
    }

    // Validação do cupom (se preenchido)
    if (coupon.trim()) {
      try {
        const couponData = await apiRequest(`/coupons/validate/${encodeURIComponent(coupon.trim())}`);
        if (!couponData.success || !couponData.valid) {
          setError(t('signup.invalidCoupon'));
          return;
        }
        referalIdToUse = couponData.referalId || null;
      } catch (error) {
        setError(t('signup.invalidCoupon'));
        return;
      }
    }

    // Cria usuário no Auth
    try {
      const result = await signup(email, password);
      if (!result.success) {
        setError(result.message);
        return;
      }

      // Pega o id do usuário criado
      const userId = result.user?.id;
      const userEmail = result.user?.email;
      if (!userId || !userEmail) {
        setError(t('signup.errorCreate'));
        return;
      }

      // Pega utm_source da URL (ex: ?utm_source=google)
      const params = new URLSearchParams(window.location.search);
      const utm_source = params.get('utm_source') || '';

      // Envia os dados do usuário para o backend
      try {
        const userData = await apiRequest('/users', {
          method: 'POST',
          body: JSON.stringify({
            id: userId,
            name: name,
            email: userEmail,
            phone_number: phone,
            coupon_code_used: coupon || null,
            utm_source,
            referal_id_used: referalIdToUse,
          }),
        });

        if (!userData.success) {
          console.error('Erro ao criar usuário no backend');
          setError(t('signup.errorSave'));
        } else {
          setUserId(userId);

          // Envia notificação para o webhook externo
          try {
            await fetch('https://automacao.wappfy.com.br/webhook/subscription?type=registred_account', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({
                name: name,
                email: userEmail
              })
            });
          } catch (webhookErr) {
            // Apenas loga, não impede o fluxo do usuário
            console.error('Erro ao enviar webhook de registro:', webhookErr);
          }

          navigate('/home');
        }
      } catch (dbError) {
        console.error('Erro ao inserir usuário:', dbError);
        setError(t('signup.errorSave') + ' Detalhe: ' + dbError.message);
      }
    } catch (err) {
      setError(t('signup.errorCreate') + ' Detalhe: ' + (err?.message || err));
      console.error('Erro inesperado ao criar usuário:', err);
    }
  };

  // Função para gerar um valor aleatório e atualizar o campo stripe_id
  const handleAddRandomStripeId = async () => {
    if (!userId) {
      alert('Usuário não identificado.');
      return;
    }
    const randomId = Math.random().toString(36).substring(2, 18);
    try {
      const data = await apiRequest(`/users/${userId}/stripe-id`, {
        method: 'PATCH',
        body: JSON.stringify({ stripeId: randomId }),
      });

      if (data.success) {
        setIdStripe(randomId);
        alert('stripe_id atualizado com sucesso!');
      } else {
        alert('Erro ao atualizar stripe_id');
      }
    } catch (error) {
      alert('Erro ao atualizar stripe_id');
      console.error('Erro:', error);
    }
  };

  const CurrentFlag = languageOptions[language]?.flag;

  return (
    <div className="signup-page">
      <div className="signup-container">
        <div className="logo-section">
          <div className="logo-wrapper">
            <span className="logo-text">Wappfy</span>
          </div>
          <p className="logo-subtitle">
            {t('signup.fillInfoText', 'Preencha abaixo com suas informações para criar sua conta e')}
          </p>
          <p className="logo-subtitle-highlight">
            {t('signup.bonusText', 'receber um bônus com dois dias de uso gratúito!')}
          </p>
        </div>

        {/* Seletor de idioma estilo Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18, position: 'relative' }} ref={dropdownRef}>
          <button
            type="button"
            className="language-button"
            style={{
              display: 'flex',
              alignItems: 'center',
              background: '#23272f',
              border: '1px solid #4B5563',
              borderRadius: 6,
              padding: '6px 12px',
              color: '#fff',
              fontSize: 15,
              cursor: 'pointer'
            }}
            onClick={() => setShowDropdown(!showDropdown)}
            title={languageOptions[language]?.country}
          >
            {CurrentFlag && <CurrentFlag className="flag-icon" style={{ width: 22, marginRight: 8 }} />}
            <span>{languageOptions[language]?.label}</span>
            <svg style={{ marginLeft: 8, width: 14, height: 14, fill: '#fff' }} viewBox="0 0 20 20"><path d="M5.23 7.21a.75.75 0 0 1 1.06.02L10 11.06l3.71-3.83a.75.75 0 1 1 1.08 1.04l-4.25 4.39a.75.75 0 0 1-1.08 0L5.21 8.27a.75.75 0 0 1 .02-1.06z" /></svg>
          </button>
          {showDropdown && (
            <div
              style={{
                position: 'absolute',
                top: 38,
                left: 0,
                background: '#23272f',
                border: '1px solid #4B5563',
                borderRadius: 8,
                zIndex: 10,
                minWidth: 160,
                boxShadow: '0 4px 16px rgba(0,0,0,0.15)'
              }}
            >
              {Object.entries(languageOptions).map(([code, { label, flag: Flag, country }]) => (
                <button
                  key={code}
                  onClick={() => handleLanguageChange(code)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    width: '100%',
                    background: 'none',
                    border: 'none',
                    color: '#fff',
                    fontSize: 15,
                    padding: '10px 16px',
                    cursor: 'pointer',
                    borderBottom: code !== 'en' ? '1px solid #374151' : 'none',
                    fontWeight: code === language ? 700 : 400
                  }}
                  title={country}
                >
                  <Flag className="flag-icon" style={{ width: 22 }} />
                  <span>{label}</span>
                  {code === language && <span style={{ marginLeft: 'auto', color: '#10b981' }}>✓</span>}
                </button>
              ))}
            </div>
          )}
        </div>

        <form onSubmit={handleSubmit} className="signup-form">
          <div className="input-group">
            <label className="input-label">{t('signup.name')}</label>
            <div className="input-wrapper">
              <FontAwesomeIcon icon={faUser} className="input-icon" />
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="input-field"
                placeholder={t('signup.placeholderName')}
                required
              />
            </div>
          </div>

          <div className="input-group">
            <label className="input-label">{t('signup.email')}</label>
            <div className="input-wrapper">
              <FontAwesomeIcon icon={faEnvelope} className="input-icon" />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input-field"
                placeholder={t('signup.placeholderEmail')}
                required
              />
            </div>
          </div>

          <div className="input-group">
            <label className="input-label">{t('signup.phone')}</label>
            <div className="input-wrapper">
              <FontAwesomeIcon icon={faPhone} className="input-icon" />
              <input
                type="tel"
                value={phone}
                onChange={e => setPhone(e.target.value.replace(/\D/g, '').slice(0, 18))}
                className="input-field"
                required
              />
            </div>
          </div>

          <div className="input-group">
            <label className="input-label">
              {t('signup.coupon')} <span style={{ color: '#9CA3AF', fontSize: 12 }}>{t('signup.couponOptional')}</span>
            </label>
            <div className="input-wrapper">
              <FontAwesomeIcon icon={faGift} className="input-icon" />
              <input
                type="text"
                value={coupon}
                onChange={e => setCoupon(e.target.value)}
                className="input-field"
                placeholder={t('signup.placeholderCoupon')}
              />
            </div>
          </div>

          <div className="input-group">
            <label className="input-label">{t('signup.password')}</label>
            <div className="input-wrapper password-wrapper">
              <FontAwesomeIcon icon={faLock} className="input-icon" />
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input-field"
                placeholder={t('signup.placeholderPassword')}
                required
                minLength={6}
              />
              <FontAwesomeIcon
                icon={showPassword ? faEyeSlash : faEye}
                className="password-toggle-icon"
                onClick={() => setShowPassword(!showPassword)}
              />
            </div>
          </div>

          {error && <p className="error-message">{error}</p>}

          <Button type="submit" className="signup-button">
            {t('signup.createAccount')}
          </Button>

          <div className="login-link">
            <p className="login-text">
              {t('signup.alreadyHave')}{' '}
              <span className="login-link-text" onClick={() => navigate('/')}>{t('signup.login')}</span>
            </p>
          </div>
        </form>
        {/* Botão para adicionar valor aleatório em stripe_id apenas se stripe_id estiver vazio */}
        {userId && !idStripe && (
          <button
            type="button"
            style={{
              marginTop: 20,
              padding: '10px 18px',
              background: '#10b981',
              color: '#fff',
              border: 'none',
              borderRadius: 6,
              fontSize: 16,
              cursor: 'pointer'
            }}
            onClick={handleAddRandomStripeId}
          >
            {t('signup.generateStripeId', 'Gerar stripe_id aleatório')}
          </button>
        )}
      </div>
    </div>
  );
};

export default Signup;
